/**
 * Dedicated challenge scorer — layout/UX aligned with fixture scorer (fixtures/scorer.html + fixtureScore).
 */
import { Alpine } from '../../core/alpine-core.js';
import { viteEnv } from '../../core/import-meta-env.js';
import { resolveVenueKioskAppOrigin } from '../../public/kiosk-tablet-api.js';
import { apiFetch, getAccessToken } from '../api.js';
import { nextBotScoreClient } from '../training/training-bot.js';
import {
  SCORER_QUICK_PRESET_POOL,
  defaultScorerQuickConfig,
  loadScorerQuickConfig,
  mergeMoreQuickPresets,
  quickPresetButtonLabel,
  saveScorerQuickConfig,
  withNearCheckoutNsOnFourthQuickTop,
} from '../scorer-quick-config.js';
import { OPPONENT_THINKING_MIN_MS, withOpponentThinkingUi } from '../../core/opponent-thinking.js';
import {
  SCORER_PLAYER_STATS_CAROUSEL_STATE,
  createScorerPlayerStatsCarouselMethods,
} from '../../shared/scorer-player-stats-carousel.js';
import { createScorerMatchScoreboardBindings } from '../../shared/scorer-match-scoreboard.js';
import { createScorerCoreMethods } from '../../shared/scorer-core.js';
import { checkoutRuleShortLabel } from '../../shared/match-format-rule-labels.js';
import QRCode from 'qrcode';

const CHALLENGE_SCORER_QUICK_LS = 'pdl.challenge-scorer.quick';

function parseChallengeId() {
  const parts = location.pathname.split('/').filter(Boolean);
  let idx = parts.indexOf('challenge-score');
  if (idx >= 0 && parts[idx + 1]) return decodeURIComponent(parts[idx + 1]);
  idx = parts.indexOf('challenges');
  if (idx < 0 || !parts[idx + 1]) return '';
  return decodeURIComponent(parts[idx + 1]);
}

function routerNavigate(path) {
  const router = window.PineconeRouter;
  if (router?.navigate) router.navigate(path);
  else window.location.href = path;
}

function playerName(player) {
  if (!player) return 'Player';
  if (player.isGuest) return player.fullName || 'Guest';
  if (player.nickname) return `${player.fullName} (${player.nickname})`;
  return player.fullName || 'Player';
}

function makeChallengeScoringDeviceId(challengeId) {
  try {
    const key = `pdl.challenge-scorer-id.${challengeId}`;
    const existing = window.localStorage.getItem(key);
    if (existing) return existing;
    const created = `challenge-${challengeId}-${Math.random().toString(36).slice(2, 10)}`;
    window.localStorage.setItem(key, created);
    return created;
  } catch {
    return `challenge-${challengeId}-scorer`;
  }
}

Alpine.data('challengeScorer', () => ({
  ...SCORER_PLAYER_STATS_CAROUSEL_STATE,
  ...createScorerCoreMethods({ scorerContext: 'challenge' }),
  challengeId: '',
  challenge: null,
  session: null,
  loading: true,
  saving: false,
  error: '',
  eventSource: null,
  eventStreamHealthy: false,
  scoringDeviceId: '',
  scoringDeviceLabel: '',
  scoreInput: '',
  checkoutDarts: '',
  checkoutLabel: '',
  checkoutConfirmOpen: false,
  checkoutConfirmPending: null,
  checkoutConfirmDarts: '3',
  quickScoresExpanded: false,
  quickScoreTop: [],
  quickScoreMore: [],
  quickConfigOpen: false,
  jumpMenuOpen: false,
  fullscreenActive: false,
  /** Venue kiosk iframe (`?embed=1`): compact chrome, no portrait gate. */
  embedMode: false,
  /** `?from=venue-kiosk` when opened from the venue tablet. */
  fromVenueKiosk: false,
  /** Venue iframe: confirm leaving scorer (notify parent shell → Challenges hub). */
  venueEmbedLeaveDialogOpen: false,
  /** Venue kiosk embed: TV + QR rail expanded (synced with parent shell). */
  venueRailExpanded: false,
  _vkShellMessageListener: null,
  /** Kiosk-style header Info sheet */
  challengeInfoSheetOpen: false,
  scorerHeaderMenuOpen: false,
  _scrollLockApplied: false,
  _prevBodyOverflow: '',
  _prevBodyTouchAction: '',
  _prevBodyOverscroll: '',
  _prevDocOverflow: '',
  _prevDocOverscroll: '',
  scorerHeaderCompact: false,
  scorerMobileTopBar: false,
  undoArmed: false,
  undoArmTimer: null,
  editingVisitIndex: -1,
  editingVisitPreScore: null,
  challengeScoringHeartbeatTimer: null,
  handleFullscreenChange: null,
  handleScroll: null,
  handleResize: null,
  handleKeydown: null,
  _pendingChallengeNoticeToastId: '',
  scorerShareQrCanvas: null,
  challengeKioskPairSecret: '',
  challengeTvPairCode: '',
  challengeTvDisplayLabel: '',
  challengeTvPairMessage: '',
  challengeTvPairBusy: false,
  _computerThrowTimer: null,
  /** Modal while the computer opponent’s visit is posting (min visible time). */
  computerThinkingOpen: false,

  async init() {
    let embed = false;
    try {
      const u = new URL(window.location.href);
      embed = u.searchParams.get('embed') === '1';
      this.fromVenueKiosk = u.searchParams.get('from') === 'venue-kiosk';
      if (embed && !this.fromVenueKiosk && globalThis.parent !== globalThis) {
        try {
          const loc = globalThis.parent.location;
          const base = `${String(loc?.pathname || '')}${String(loc?.search || '')}`;
          if (base.includes('venue-kiosk')) this.fromVenueKiosk = true;
        } catch {
          /* cross-origin parent */
        }
      }
    } catch {
      embed = false;
      this.fromVenueKiosk = false;
    }
    this.embedMode = embed;
    this.applyScorerLayoutTheme(this.readScorerLayoutTheme());
    this.syncVenueKioskScorerEmbedTheme();
    this._vkShellMessageListener = (ev) => {
      try {
        if (!ev?.data || typeof ev.data !== 'object') return;
        const origin = String(globalThis.location?.origin || '');
        if (origin && String(ev.origin || '') !== origin) return;
        const d = ev.data;
        if (this.handleVenueKioskScorerThemeMessage(d)) return;
        if (this.handleVenueKioskShellHistoryMessage?.(d)) return;
        if (d.source === 'venue-kiosk-shell' && d.type === 'venue-kiosk-rail-state') {
          this.fromVenueKiosk = true;
          if (typeof d.expanded === 'boolean') this.venueRailExpanded = d.expanded;
        }
      } catch {
        /* ignore */
      }
    };
    globalThis.window?.addEventListener('message', this._vkShellMessageListener);

    const q0 = loadScorerQuickConfig(CHALLENGE_SCORER_QUICK_LS);
    this.quickScoreTop = q0.top;
    this.quickScoreMore = q0.more;

    if (!this.$store.features.enabled('challenges')) {
      this.error = 'Challenges are disabled.';
      this.loading = false;
      return;
    }
    this.challengeId = parseChallengeId();
    if (!this.challengeId) {
      this.error = 'Missing challenge id';
      this.loading = false;
      return;
    }
    this.applyDedicatedChromeMode(true);
    this.syncScorerTopBarViewport();
    this.scoringDeviceId = makeChallengeScoringDeviceId(this.challengeId);
    this.scoringDeviceLabel = `${navigator.platform || 'Device'} challenge scorer`;
    this.handleFullscreenChange = () => {
      this.fullscreenActive = !!document.fullscreenElement;
    };
    this.handleScroll = () => {
      this.scorerHeaderCompact = window.scrollY > 24;
    };
    this.handleResize = () => {
      this.syncScorerTopBarViewport();
    };
    if (this.handleKeydown) {
      window.removeEventListener('keydown', this.handleKeydown, true);
    }
    this.handleKeydown = (e) => this.onScoreKeydown(e);
    document.addEventListener('fullscreenchange', this.handleFullscreenChange);
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    window.addEventListener('resize', this.handleResize, { passive: true });
    window.addEventListener('keydown', this.handleKeydown, true);
    window.addEventListener('beforeunload', () => this.teardown());
    try {
      await this.refreshChallenge();
      await this.loadChallengeSession();
      if (this.isLocalChallenge() && this.isMatchLive()
        && (this.isParticipant() || this.$store.session?.me?.role === 'admin')) {
        await this.ensureChallengeScoringClaim(false);
      }
      this.ensureChallengeEventStream();
      this.ensureChallengeScoringHeartbeatLoop();
    } catch (e) {
      this.error = e.message || 'Unable to load challenge scorer';
      const stale = /not found|challenge_not_found|\b404\b/i.test(String(e?.message || ''));
      if (stale && this.embedMode && this.fromVenueKiosk) {
        this.postVenueKioskShellMessage({ type: 'challenge-scorer-leave', stale: true });
      }
    } finally {
      this.loading = false;
      this.scorerHeaderCompact = window.scrollY > 24;
      this.$nextTick(() => this.renderScorerShareQr());
    }
  },

  venueKioskHomeHref() {
    return `${resolveVenueKioskAppOrigin()}/venue-kiosk?challenges=1`;
  },

  isVenueKioskEmbed() {
    if (!this.embedMode) return false;
    if (this.fromVenueKiosk) return true;
    try {
      return globalThis.parent !== globalThis;
    } catch {
      return false;
    }
  },

  postVenueKioskShellMessage(payload) {
    const origin = globalThis.location?.origin || '*';
    try {
      if (globalThis.parent !== globalThis) {
        globalThis.parent.postMessage({ source: 'darts-scorer-embed', ...payload }, origin);
      }
    } catch {
      /* ignore */
    }
  },

  toggleVenueKioskShellRailFromEmbed() {
    if (!this.isVenueKioskEmbed()) return;
    const nextExpanded = !this.venueRailExpanded;
    this.venueRailExpanded = nextExpanded;
    this.postVenueKioskShellMessage({ type: 'toggle-venue-kiosk-rail', expanded: nextExpanded });
  },

  openVenueEmbedLeaveDialog() {
    if (!this.embedMode || !this.fromVenueKiosk) return;
    this.challengeInfoSheetOpen = false;
    this.venueEmbedLeaveDialogOpen = true;
  },

  closeVenueEmbedLeaveDialog() {
    this.venueEmbedLeaveDialogOpen = false;
  },

  async confirmVenueEmbedLeaveToChallenges() {
    if (!this.embedMode || !this.fromVenueKiosk) return;
    this.challengeInfoSheetOpen = false;
    if (this.isLocalChallenge() && this.isChallengeScoringControllerOwner()) {
      await this.releaseChallengeScoringControl();
    }
    this.venueEmbedLeaveDialogOpen = false;
    this.teardown();
    this.postVenueKioskShellMessage({ type: 'challenge-scorer-leave' });
    if (globalThis.parent === globalThis) {
      globalThis.location.href = this.venueKioskHomeHref();
    }
  },

  shareScorerPageUrl() {
    const id = this.challenge?._id || this.challengeId;
    if (!id) return '';
    const base = preferredAppOrigin();
    try {
      return new URL(`/admin/challenges/${id}/scorer`, base).href;
    } catch {
      return `${base}/admin/challenges/${id}/scorer`;
    }
  },

  showScorerShareQrStrip() {
    if (!this.challenge?._id) return false;
    const admin = this.$store.session?.me?.role === 'admin';
    if (!this.isParticipant() && !admin) return false;
    if (['declined', 'cancelled'].includes(String(this.challenge?.status || ''))) return false;
    if (this.isLocalChallenge()) {
      return true;
    }
    return this.$store.features.enabled('challengeSessions') && this.isRemoteChallenge();
  },

  async renderScorerShareQr() {
    const mount = this.$refs?.scorerShareQr;
    if (!mount || !this.shareScorerPageUrl()) return;
    if (this.scorerShareQrCanvas && !mount.contains(this.scorerShareQrCanvas)) {
      this.scorerShareQrCanvas = null;
    }
    if (!this.scorerShareQrCanvas) {
      this.scorerShareQrCanvas = document.createElement('canvas');
      this.scorerShareQrCanvas.width = 112;
      this.scorerShareQrCanvas.height = 112;
      this.scorerShareQrCanvas.className = 'block h-28 w-28 rounded-md';
      mount.innerHTML = '';
      mount.appendChild(this.scorerShareQrCanvas);
    }
    try {
      await QRCode.toCanvas(this.scorerShareQrCanvas, this.shareScorerPageUrl(), {
        width: 112,
        margin: 1,
        color: { dark: '#000000', light: '#ffffff' },
        errorCorrectionLevel: 'H',
      });
    } catch (e) {
      console.error('Failed to render scorer share QR', e);
    }
  },

  teardown() {
    this.applyDedicatedChromeMode(false);
    if (this.challengeScoringHeartbeatTimer) {
      window.clearInterval(this.challengeScoringHeartbeatTimer);
      this.challengeScoringHeartbeatTimer = null;
    }
    if (this.undoArmTimer) {
      window.clearTimeout(this.undoArmTimer);
      this.undoArmTimer = null;
    }
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
    if (this.handleFullscreenChange) {
      document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
      this.handleFullscreenChange = null;
    }
    if (this.handleScroll) {
      window.removeEventListener('scroll', this.handleScroll);
      this.handleScroll = null;
    }
    if (this.handleResize) {
      window.removeEventListener('resize', this.handleResize);
      this.handleResize = null;
    }
    if (this.handleKeydown) {
      window.removeEventListener('keydown', this.handleKeydown, true);
      this.handleKeydown = null;
    }
    if (this._vkShellMessageListener) {
      globalThis.window?.removeEventListener('message', this._vkShellMessageListener);
      this._vkShellMessageListener = null;
    }
    if (this._computerThrowTimer) {
      window.clearTimeout(this._computerThrowTimer);
      this._computerThrowTimer = null;
    }
    this.challengeInfoSheetOpen = false;
    this.venueEmbedLeaveDialogOpen = false;
    if (typeof document !== 'undefined' && this._scrollLockApplied) {
      const body = document.body;
      const docEl = document.documentElement;
      if (body && docEl) {
        body.style.overflow = this._prevBodyOverflow || '';
        body.style.touchAction = this._prevBodyTouchAction || '';
        body.style.overscrollBehavior = this._prevBodyOverscroll || '';
        docEl.style.overflow = this._prevDocOverflow || '';
        docEl.style.overscrollBehavior = this._prevDocOverscroll || '';
      }
      this._scrollLockApplied = false;
    }
  },

  refPlayerId(playerRef) {
    return String(playerRef?._id || playerRef || '');
  },

  currentPlayerId() {
    const store = this.$store.session;
    const me = store?.me || {};
    return String(store?.rosterSelfPlayerId || me.playerId || me._id || '').trim();
  },

  isParticipant() {
    const uid = this.currentPlayerId();
    const a = this.refPlayerId(this.challenge?.challengerPlayerId);
    const b = this.refPlayerId(this.challenge?.challengedPlayerId);
    return [a, b].includes(uid);
  },

  isChallengedPlayer() {
    return this.refPlayerId(this.challenge?.challengedPlayerId) === this.currentPlayerId();
  },

  canRespondToPendingChallenge() {
    const pending = String(this.challenge?.status || '') === 'pending';
    if (!pending) return false;
    if (this.$store.session?.me?.role === 'admin') return true;
    return this.isChallengedPlayer();
  },

  challengePendingWaitingOnOpponent() {
    if (this.$store.session?.me?.role === 'admin') return false;
    return String(this.challenge?.status || '') === 'pending'
      && this.isParticipant()
      && !this.isChallengedPlayer();
  },

  maybeToastPendingChallengeNotice() {
    const id = String(this.challenge?._id || this.challengeId || '');
    if (!id || String(this.challenge?.status || '') !== 'pending') return;
    if (this._pendingChallengeNoticeToastId === id) return;
    const t = this.$store.toastr;
    if (this.challengePendingWaitingOnOpponent()) {
      const opp = playerName(this.challenge.challengedPlayerId);
      t.info(`${opp} must accept or reject this challenge before play can start. Open the challenge console if they need the link.`, 7000);
      this._pendingChallengeNoticeToastId = id;
      return;
    }
    if (this.canRespondToPendingChallenge()) {
      if (this.isChallengedPlayer()) {
        t.info('This challenge needs your response — open the console and use Accept or Reject in the top bar.', 6000);
      } else {
        t.info('This challenge is pending — open the console and use Accept or Reject in the top bar.', 6000);
      }
      this._pendingChallengeNoticeToastId = id;
    }
  },

  isLocalChallenge() {
    return String(this.challenge?.mode || '') === 'local';
  },

  isRemoteChallenge() {
    return String(this.challenge?.mode || '') === 'remote';
  },

  hasMediaSession() {
    return this.challenge?.mode === 'remote' && !!this.session;
  },

  liveScoring() {
    return this.challenge?.liveScoring || {};
  },

  liveLegs() {
    return this.liveScoring().legs || [];
  },

  currentLiveLeg() {
    return this.liveLegs().find((leg) => !leg.complete) || this.liveLegs()[this.liveLegs().length - 1] || null;
  },

  currentTurnPlayerId() {
    return String(this.liveScoring().currentTurnPlayerId || '');
  },

  isMyTurn() {
    return this.currentPlayerId() === this.currentTurnPlayerId();
  },

  challengePlayerForId(id) {
    const sid = String(id || '');
    if (!sid) return null;
    if (this.refPlayerId(this.challenge?.challengerPlayerId) === sid) return this.challenge?.challengerPlayerId || null;
    if (this.refPlayerId(this.challenge?.challengedPlayerId) === sid) return this.challenge?.challengedPlayerId || null;
    return null;
  },

  challengePlayerIsComputer(id) {
    const p = this.challengePlayerForId(id);
    if (!p) return false;
    return /^computer\b/i.test(String(playerName(p) || '').trim());
  },

  challengeComputerLevelForTurn() {
    const id = this.currentTurnPlayerId();
    const p = this.challengePlayerForId(id);
    const label = String(playerName(p) || '');
    const m = label.match(/\(L\s*(\d{1,2})\)/i);
    const n = Math.round(Number(m?.[1] || 5));
    if (!Number.isFinite(n)) return 5;
    return Math.min(10, Math.max(1, n));
  },

  playerRemaining(playerId) {
    return Number(this.liveScoring()?.currentRemaining?.[String(playerId)]
      ?? this.liveScoring()?.startingScore ?? 501);
  },

  canUseLiveScoring() {
    const s = String(this.challenge?.status || '');
    if (['declined', 'cancelled', 'confirmed'].includes(s)) return false;
    if (this.$store.session?.me?.role === 'admin') return true;
    return this.isParticipant();
  },

  isMatchLive() {
    if (this.challenge?.liveScoring?.matchFinished) return false;
    if (this.session && this.hasMediaSession()) {
      return String(this.session?.status || '') === 'live';
    }
    const s = String(this.challenge?.status || '');
    if (['accepted', 'in_progress', 'submitted', 'disputed'].includes(s)) return true;
    // Venue shell embed (`?embed=1&from=venue-kiosk`): play on the board immediately; no console accept step.
    if (this.embedMode && this.fromVenueKiosk && this.isLocalChallenge()) {
      return !['declined', 'cancelled', 'confirmed'].includes(s);
    }
    return false;
  },

  currentChallengeScoringControl() {
    const c = this.challenge?.liveScoring?.scoringControl;
    const exp = c?.expiresAt ? new Date(c.expiresAt).getTime() : 0;
    if (!c?.deviceId || !exp || exp <= Date.now()) return null;
    return c;
  },

  isChallengeScoringControllerOwner() {
    if (!this.isLocalChallenge()) return true;
    const owner = this.currentChallengeScoringControl();
    if (!owner) return true;
    return String(owner.deviceId || '') === String(this.scoringDeviceId || '');
  },

  /** Active local scoring lock (another device or this device holds the keypad lease). */
  challengeLocalScoringLockActive() {
    return this.isLocalChallenge() && !!this.currentChallengeScoringControl();
  },

  /** Fixed bottom bar + aside lock card: show when the venue lock is active and scoring could matter. */
  challengeScorerLockUiVisible() {
    if (!this.challengeLocalScoringLockActive()) return false;
    if (!this.challengesScorerReady() || !this.hasRequiredPlayers()) return false;
    return true;
  },

  /** "Take control" in overlay when this device is blocked by another holder. */
  scorerOverlayTakeControlVisible() {
    return (
      this.scorerOverlayTakeControlEligible()
      && !!this.currentChallengeScoringControl()
      && !this.isChallengeScoringControllerOwner()
    );
  },

  scorerOverlayTakeControlEligible() {
    return (
      this.isLocalChallenge()
      && this.challengesScorerReady()
      && this.hasRequiredPlayers()
    );
  },

  /** Show release when this device holds the lock, or admin may clear any active lock. */
  challengeScorerReleaseControlVisible() {
    if (!this.challengeScorerLockUiVisible() || !this.currentChallengeScoringControl()) return false;
    if (this.$store.session?.me?.role === 'admin') return true;
    return this.isChallengeScoringControllerOwner();
  },

  /** Show take when someone else holds the lock. */
  challengeScorerTakeControlVisible() {
    return (
      this.challengeScorerLockUiVisible()
      && !!this.currentChallengeScoringControl()
      && !this.isChallengeScoringControllerOwner()
    );
  },

  canScoreNow() {
    if (!this.canUseLiveScoring() || !this.isMatchLive()) return false;
    if (this.isRemoteChallenge()) return this.isMyTurn();
    return this.isChallengeScoringControllerOwner();
  },

  canAutoComputerThrowNow() {
    if (!this.isLocalChallenge()) return false;
    if (!this.canScoreNow() || !this.hasRequiredPlayers()) return false;
    if (this.computerThinkingOpen) return false;
    if (this.editingVisitIndex >= 0 || this.saving) return false;
    const turn = this.currentTurnPlayerId();
    if (!turn) return false;
    return this.challengePlayerIsComputer(turn);
  },

  queueComputerThrowIfNeeded() {
    if (!this.canAutoComputerThrowNow()) {
      if (this._computerThrowTimer) {
        window.clearTimeout(this._computerThrowTimer);
        this._computerThrowTimer = null;
      }
      return;
    }
    if (this._computerThrowTimer) return;
    this._computerThrowTimer = window.setTimeout(async () => {
      this._computerThrowTimer = null;
      await this.recordComputerThrow();
    }, 550 + Math.floor(Math.random() * 450));
  },

  async recordComputerThrow() {
    if (!this.canAutoComputerThrowNow()) return;
    this.disarmUndo();
    this.saving = true;
    try {
      await withOpponentThinkingUi(
        OPPONENT_THINKING_MIN_MS,
        (open) => {
          this.computerThinkingOpen = !!open;
        },
        async () => {
          const remaining = Math.max(0, Number(this.currentTurnRemaining()) || 0);
          const score = Math.min(180, Math.max(0, nextBotScoreClient(remaining, this.rule(), this.challengeComputerLevelForTurn())));
          const nextRemaining = remaining - score;
          const checkout = nextRemaining === 0 && score > 0;
          const body = {
            score,
            checkoutDarts: checkout ? 3 : null,
            checkoutLabel: checkout ? String(score) : undefined,
          };
          const res = await apiFetch(`/challenges/${this.challenge._id}/live/visit`, {
            method: 'POST',
            body: this.isLocalChallenge() ? { ...body, ...this.scoringControlBody() } : body,
          });
          const data = await res.json();
          if (!res.ok || !data?.ok) throw new Error(data?.message || 'Failed to record computer visit');
          this.challenge = data.challenge || this.challenge;
          this.ensureChallengeScoringHeartbeatLoop();
        },
      );
      this.queueComputerThrowIfNeeded();
    } catch {
      /* keep scorer usable even if one bot throw fails */
    } finally {
      this.saving = false;
    }
  },

  scoringControlBody(extra = {}) {
    return {
      deviceId: this.scoringDeviceId,
      deviceLabel: this.scoringDeviceLabel,
      ...extra,
    };
  },

  challengesScorerReady() {
    return this.canUseLiveScoring() && !this.challenge?.liveScoring?.matchFinished && this.isMatchLive();
  },

  hasRequiredPlayers() {
    return !!(this.refPlayerId(this.challenge?.challengerPlayerId)
      && this.refPlayerId(this.challenge?.challengedPlayerId));
  },

  scorerBlockedMessage() {
    if (!this.canUseLiveScoring()) return 'You cannot score this challenge';
    if (this.challenge?.liveScoring?.matchFinished) return 'This match is finished';
    if (!this.isMatchLive()) {
      if (this.isRemoteChallenge() && this.session && String(this.session?.status || '') !== 'live') {
        return 'Video session not live yet';
      }
      return 'Match is not live yet';
    }
    if (!this.canScoreNow()) {
      if (this.isLocalChallenge()) return 'This scorer is locked to another device';
      return 'Waiting for your turn to throw';
    }
    return '';
  },

  scorerNotLiveDetail() {
    if (this.isRemoteChallenge() && this.session && String(this.session?.status || '') !== 'live') {
      return 'On the challenge console, open Challenge Session: connect both cameras, then each player taps Ready. When the status shows Live, scoring unlocks here.';
    }
    if (this.isRemoteChallenge() && !this.session) {
      return 'On the challenge console, open Challenge Session and wait for it to load, then connect cameras and both tap Ready.';
    }
    if (String(this.challenge?.status || '') === 'pending') {
      if (this.isLocalChallenge()) {
        return 'Accept the challenge on the console. Local matches need no Ready or camera step — scoring unlocks as soon as it is accepted.';
      }
      return 'The challenge must be accepted on the console before the match can go live.';
    }
    if (this.isLocalChallenge()) {
      return 'Open the challenge console. Local matches have no Ready step — ensure the challenge is accepted, then return here.';
    }
    return 'Open the challenge console, finish any pending steps there, then return here.';
  },

  scorerBlockedDetail() {
    if (!this.canUseLiveScoring()) return 'Sign in as a player or admin.';
    if (this.challenge?.liveScoring?.matchFinished) return 'Open the challenge console to review or submit.';
    if (!this.isMatchLive()) {
      return this.scorerNotLiveDetail();
    }
    if (this.isLocalChallenge() && !this.isChallengeScoringControllerOwner()) {
      const c = this.currentChallengeScoringControl();
      if (!c) return 'Claim control from the panel on the right.';
      return `Active scorer: ${c.userName || c.label || c.deviceId}`;
    }
    if (this.isRemoteChallenge() && !this.isMyTurn()) {
      return 'The other player is throwing.';
    }
    return '';
  },

  scorerOverlayVisible() {
    return !!this.challenge && (!this.challengesScorerReady() || !this.canScoreNow());
  },

  legPointsChallenger() {
    const id = this.refPlayerId(this.challenge?.challengerPlayerId);
    const row = (this.liveScoring().players || []).find((p) => this.refPlayerId(p.playerId) === id);
    return Number(row?.legsWon) || 0;
  },

  legPointsChallenged() {
    const id = this.refPlayerId(this.challenge?.challengedPlayerId);
    const row = (this.liveScoring().players || []).find((p) => this.refPlayerId(p.playerId) === id);
    return Number(row?.legsWon) || 0;
  },

  /** Match fixture `currentGameBadge()` — leg index on challenge scorer. */
  challengeScorerGameBadge() {
    const n = Number(this.currentLiveLeg()?.legNumber || 1);
    return `L${Number.isFinite(n) && n > 0 ? n : 1}`;
  },

  legsNeededToWinMatchChallenge() {
    const bo = Math.max(1, Number(this.challenge?.format?.bestOfLegs) || 9);
    return Math.floor(bo / 2) + 1;
  },

  challengeHeaderRaceLeftText() {
    if (this.challengeFrozenForScoring()) return '—';
    const need = this.legsNeededToWinMatchChallenge();
    const h = this.legPointsChallenger();
    const a = this.legPointsChallenged();
    const max = Math.max(h, a);
    const left = Math.max(0, need - max);
    if (left <= 0) return 'Deciding';
    return left === 1 ? '1 leg for match' : `${left} legs for match`;
  },

  headerScoreForIndex(i) {
    if (Number(i) === 1) return this.legPointsChallenged();
    return this.legPointsChallenger();
  },

  headerHomeTeamDisplay() {
    return String(this.sidePlayerLabel('home') || '').trim() || 'Challenger';
  },

  headerAwayTeamDisplay() {
    return String(this.sidePlayerLabel('away') || '').trim() || 'Challenged';
  },

  challengeMaxThrownLimit() {
    const n = Math.round(Number(this.challenge?.format?.maxThrown));
    return Number.isFinite(n) ? Math.min(999, Math.max(9, n)) : 51;
  },

  legDartsFractionDisplayChallenge() {
    return `${this.currentLegDartsThrown()} / ${this.challengeMaxThrownLimit()}`;
  },

  challengeCheckoutRuleLabel() {
    return checkoutRuleShortLabel(this.challenge?.format?.checkout);
  },

  lastThreeVisitPillsForChallengeSide(side) {
    const homeId = this.refPlayerId(this.challenge?.challengerPlayerId);
    const awayId = this.refPlayerId(this.challenge?.challengedPlayerId);
    const targetId = side === 'home' ? homeId : awayId;
    const all = this.currentLiveLeg()?.visits || [];
    const forSide = all.filter((v) => this.refPlayerId(v?.playerId) === targetId);
    return forSide.slice(-3).map((v) => this.visitCompactDisplay(v));
  },

  toggleChallengeInfoSheet() {
    this.scorerHeaderMenuOpen = false;
    this.challengeInfoSheetOpen = !this.challengeInfoSheetOpen;
  },

  closeChallengeInfoSheet() {
    this.challengeInfoSheetOpen = false;
  },

  hasChallengeScrollLockOverlays() {
    return !!(this.computerThinkingOpen || this.challengeInfoSheetOpen || this.venueEmbedLeaveDialogOpen);
  },

  syncChallengeOverlayScrollLock() {
    if (typeof document === 'undefined') return;
    const shouldLock = this.hasChallengeScrollLockOverlays();
    if (shouldLock === this._scrollLockApplied) return;
    const body = document.body;
    const docEl = document.documentElement;
    if (!body || !docEl) return;
    if (shouldLock) {
      this._prevBodyOverflow = body.style.overflow;
      this._prevBodyTouchAction = body.style.touchAction;
      this._prevBodyOverscroll = body.style.overscrollBehavior;
      this._prevDocOverflow = docEl.style.overflow;
      this._prevDocOverscroll = docEl.style.overscrollBehavior;
      body.style.overflow = 'hidden';
      body.style.touchAction = 'none';
      body.style.overscrollBehavior = 'none';
      docEl.style.overflow = 'hidden';
      docEl.style.overscrollBehavior = 'none';
    } else {
      body.style.overflow = this._prevBodyOverflow || '';
      body.style.touchAction = this._prevBodyTouchAction || '';
      body.style.overscrollBehavior = this._prevBodyOverscroll || '';
      docEl.style.overflow = this._prevDocOverflow || '';
      docEl.style.overscrollBehavior = this._prevDocOverscroll || '';
    }
    this._scrollLockApplied = shouldLock;
  },

  challengeFrozenForScoring() {
    return !!this.challenge?.liveScoring?.matchFinished;
  },

  syncScorerTopBarViewport() {
    try {
      this.scorerMobileTopBar = window.matchMedia('(max-width:639px)').matches;
    } catch {
      this.scorerMobileTopBar = false;
    }
  },

  scorerTopBarRowPaddingStyle() {
    const dedicated = true;
    if (this.scorerMobileTopBar) {
      if (dedicated) {
        if (this.scorerHeaderCompact) return 'padding-top:0.1875rem;padding-bottom:0.1875rem;';
        return 'padding-top:0.3125rem;padding-bottom:0.3125rem;';
      }
      if (this.scorerHeaderCompact) return 'padding-top:0.2625rem;padding-bottom:0.2625rem;';
      return 'padding-top:0.4625rem;padding-bottom:0.4625rem;';
    }
    if (dedicated) {
      if (this.scorerHeaderCompact) return 'padding-top:0.35rem;padding-bottom:0.35rem;';
      return 'padding-top:0.525rem;padding-bottom:0.525rem;';
    }
    if (this.scorerHeaderCompact) return 'padding-top:0.45rem;padding-bottom:0.45rem;';
    return 'padding-top:0.65rem;padding-bottom:0.65rem;';
  },

  visitSideFromVisit(visit) {
    const id = this.refPlayerId(visit?.playerId);
    if (!id) return '';
    return id === this.refPlayerId(this.challenge?.challengerPlayerId) ? 'home' : 'away';
  },

  lastVisitsForSide(side, limit = 3) {
    const cap = Math.max(0, Number(limit) || 3);
    const homeId = this.refPlayerId(this.challenge?.challengerPlayerId);
    const awayId = this.refPlayerId(this.challenge?.challengedPlayerId);
    const targetId = side === 'home' ? homeId : awayId;
    const all = this.currentLiveLeg()?.visits || [];
    const forSide = all
      .map((visit, index) => ({ visit, index }))
      .filter(({ visit }) => this.refPlayerId(visit?.playerId) === targetId);
    return forSide.slice(-cap).reverse();
  },

  visitCompactDisplay(visit) {
    if (!visit) return '';
    if (visit.bust) return 'Bust';
    const score = Number(visit.score || 0);
    if (visit.checkout) {
      const d = Math.max(1, Number(visit.darts || 3));
      return `${score}/${d}d`;
    }
    return String(score);
  },

  historyVisitActorLabel(visit) {
    return this.visitDisplayName(visit);
  },

  currentTurnSide() {
    const turn = this.currentTurnPlayerId();
    if (!turn) return 'home';
    return turn === this.refPlayerId(this.challenge?.challengerPlayerId) ? 'home' : 'away';
  },

  sidePlayerLabel(side) {
    const p = side === 'home' ? this.challenge?.challengerPlayerId : this.challenge?.challengedPlayerId;
    return playerName(p);
  },

  sidePlayerAvatar(side) {
    const p = side === 'home' ? this.challenge?.challengerPlayerId : this.challenge?.challengedPlayerId;
    return p?.imageUrl || '/assets/placeholder-player.png';
  },

  teamLogo(side) {
    return this.sidePlayerAvatar(side);
  },

  remainingForSide(side) {
    const p = side === 'home' ? this.challenge?.challengerPlayerId : this.challenge?.challengedPlayerId;
    return this.playerRemaining(this.refPlayerId(p));
  },

  currentLegHighScore() {
    const visits = this.liveLegs().flatMap((leg) => leg?.visits || []);
    return visits.reduce((best, v) => (v?.bust ? best : Math.max(best, Number(v?.score || 0))), 0);
  },

  currentLegDartsThrown() {
    const visits = this.currentLiveLeg()?.visits || [];
    return visits.reduce((t, v) => t + Math.max(0, Number(v?.darts || 3)), 0);
  },

  historyVisits() {
    const visits = this.currentLiveLeg()?.visits || [];
    return visits.map((visit, index) => ({ visit, index })).reverse();
  },

  visitDisplayName(visit) {
    return playerName(visit?.playerId) || this.playerLabelById(this.refPlayerId(visit?.playerId));
  },

  playerLabelById(id) {
    if (!id) return '';
    if (id === this.refPlayerId(this.challenge?.challengerPlayerId)) return this.sidePlayerLabel('home');
    if (id === this.refPlayerId(this.challenge?.challengedPlayerId)) return this.sidePlayerLabel('away');
    return 'Player';
  },

  allQuickPresetPool() {
    return [...SCORER_QUICK_PRESET_POOL];
  },

  quickPresetButtonLabel,

  visibleQuickPresets() {
    return this.quickScoreTop.slice(0, 4);
  },

  /** Always 4 slots (null = empty) for a stable 4 + More row. */
  paddedVisibleQuickPresets() {
    const v = this.visibleQuickPresets();
    return [0, 1, 2, 3].map((i) => (v[i] != null ? v[i] : null));
  },

  keypadQuickTopSlots() {
    const padded = this.paddedVisibleQuickPresets();
    const editing = this.editingVisitIndex >= 0;
    const rem = this.currentTurnRemaining();
    const active = this.canScoreNow() && this.hasRequiredPlayers() && !editing && !this.saving;
    return withNearCheckoutNsOnFourthQuickTop(padded, rem, { editingVisit: editing, active });
  },

  onQuickTopSlotClick(preset) {
    if (preset && typeof preset === 'object' && preset.ns) {
      void this.quickTopNoScoreAndSubmit();
      return;
    }
    this.setScoreInput(preset);
  },

  async quickTopNoScoreAndSubmit() {
    if (!this.scoringKeypadActive()) return;
    if (this.editingVisitIndex >= 0) return;
    this.setScoreInput(0);
    await this.$nextTick();
    await this.recordThrow();
  },

  moreQuickPresets() {
    return mergeMoreQuickPresets(this.quickScoreMore);
  },

  hasMoreQuickPresets() {
    return this.moreQuickPresets().length > 0;
  },

  persistQuickScores() {
    saveScorerQuickConfig(CHALLENGE_SCORER_QUICK_LS, { top: this.quickScoreTop, more: this.quickScoreMore });
  },

  toggleQuickTop(preset) {
    const p = Number(preset);
    const top = [...this.quickScoreTop];
    const more = [...this.quickScoreMore];
    const i = top.indexOf(p);
    if (i >= 0) {
      top.splice(i, 1);
    } else if (top.length >= 4) {
      Alpine.store('toastr')?.info?.('At most 4 scores on the top row.', 2800);
      return;
    } else {
      const mi = more.indexOf(p);
      if (mi >= 0) more.splice(mi, 1);
      top.push(p);
    }
    this.quickScoreTop = top;
    this.quickScoreMore = more;
    this.persistQuickScores();
  },

  toggleQuickMore(preset) {
    const p = Number(preset);
    if (this.quickScoreTop.includes(p)) return;
    const more = [...this.quickScoreMore];
    const i = more.indexOf(p);
    if (i >= 0) more.splice(i, 1);
    else more.push(p);
    more.sort((a, b) => a - b);
    this.quickScoreMore = more;
    this.persistQuickScores();
  },

  resetQuickScoresToDefault() {
    const d = defaultScorerQuickConfig();
    this.quickScoreTop = [...d.top];
    this.quickScoreMore = [...d.more];
    this.persistQuickScores();
    Alpine.store('toastr')?.success?.('Quick scores reset', 2000);
  },

  currentTurnRemaining() {
    const id = this.currentTurnPlayerId();
    if (!id) return this.liveScoring()?.startingScore || 501;
    return this.playerRemaining(id);
  },

  scoreOutcome() {
    const score = Number(this.scoreInput || 0);
    if (!Number.isFinite(score) || score < 0) {
      return { valid: false, checkout: false, busted: false, remaining: this.currentTurnRemaining() };
    }
    const remaining = this.editingVisitIndex >= 0 && Number.isFinite(Number(this.editingVisitPreScore))
      ? Number(this.editingVisitPreScore)
      : this.currentTurnRemaining();
    const nextRemaining = Math.max(0, remaining - score);
    const busted = score > remaining || nextRemaining === 1;
    const checkout = !busted && nextRemaining === 0;
    return { valid: true, checkout, busted, remaining: nextRemaining };
  },

  canSubmitVisit() {
    if (!this.canScoreNow()) return false;
    if (!this.hasRequiredPlayers()) return false;
    if (!String(this.scoreInput || '').trim()) return false;
    const outcome = this.scoreOutcome();
    if (!outcome.valid) return false;
    return true;
  },

  openCheckoutConfirmDialog() {
    this.checkoutConfirmPending = {
      scoreInput: String(this.scoreInput ?? ''),
      checkoutDarts: String(this.checkoutDarts || ''),
      checkoutLabel: String(this.checkoutLabel || ''),
    };
    this.checkoutConfirmDarts = ['1', '2', '3'].includes(String(this.checkoutDarts || ''))
      ? String(this.checkoutDarts)
      : '3';
    this.checkoutConfirmOpen = true;
  },

  cancelCheckoutConfirm() {
    const pending = this.checkoutConfirmPending || null;
    if (pending) {
      this.scoreInput = String(pending.scoreInput ?? '');
      this.checkoutDarts = String(pending.checkoutDarts || '');
      this.checkoutLabel = String(pending.checkoutLabel || '');
    }
    this.checkoutConfirmPending = null;
    this.checkoutConfirmOpen = false;
  },

  async confirmCheckoutConfirm() {
    if (!this.checkoutConfirmOpen) return;
    this.checkoutDarts = String(this.checkoutConfirmDarts || '3');
    this.checkoutConfirmPending = null;
    this.checkoutConfirmOpen = false;
    await this.recordThrow({ skipCheckoutConfirm: true });
  },

  isEditingHistoryEntry(index) {
    return Number(this.editingVisitIndex) === Number(index);
  },

  editVisit(visit, index) {
    if (!this.canScoreNow() || !this.hasRequiredPlayers()) return;
    this.scoreInput = String(Number(visit?.score || 0));
    this.editingVisitIndex = Number(index);
    this.editingVisitPreScore = Number.isFinite(Number(visit?.preScore)) ? Number(visit.preScore) : null;
    this.checkoutDarts = '';
    this.checkoutLabel = String(visit?.checkoutLabel || '').trim() || '';
  },

  cancelVisitEdit() {
    this.editingVisitIndex = -1;
    this.editingVisitPreScore = null;
    this.scoreInput = '';
    this.checkoutDarts = '';
    this.checkoutLabel = '';
  },

  async saveVisitEdit() {
    if (!this.canScoreNow() || !this.hasRequiredPlayers() || this.editingVisitIndex < 0) return;
    this.disarmUndo();
    const score = Number(this.scoreInput || 0);
    if (!Number.isFinite(score) || score < 0) return;
    this.saving = true;
    try {
      const res = await apiFetch(`/challenges/${this.challenge._id}/live/visits/${this.editingVisitIndex}`, {
        method: 'PATCH',
        body: this.isLocalChallenge() ? { score, ...this.scoringControlBody() } : { score },
      });
      const data = await res.json();
      if (!res.ok || !data?.ok) throw new Error(data?.message || 'Failed to update throw');
      this.challenge = data.challenge || this.challenge;
      this.cancelVisitEdit();
      Alpine.store('toastr').success('Throw updated', 2500);
      this.ensureChallengeScoringHeartbeatLoop();
      this.queueComputerThrowIfNeeded();
    } catch (e) {
      Alpine.store('toastr').error(e.message || 'Unable to update throw', 3000);
    } finally {
      this.saving = false;
    }
  },

  keypadUnlocked() {
    return this.canUseLiveScoring() && !this.challenge?.liveScoring?.matchFinished && this.isMatchLive();
  },

  scoringKeypadActive() {
    return this.keypadUnlocked() || this.editingVisitIndex >= 0;
  },

  appendDigit(digit) {
    if (!this.scoringKeypadActive()) return;
    const nextValue = `${this.scoreInput || ''}${digit}`.slice(0, 3);
    const numeric = Math.min(180, Number(nextValue || 0));
    this.scoreInput = String(Number.isFinite(numeric) ? numeric : '');
    this.checkoutDarts = '';
  },

  setScoreInput(value) {
    if (!this.scoringKeypadActive()) return;
    if (value && typeof value === 'object') return;
    const numeric = Math.min(180, Number(value || 0));
    this.scoreInput = String(Number.isFinite(numeric) ? numeric : '');
    this.checkoutDarts = '';
  },

  clearScoreInput() {
    if (!this.scoringKeypadActive()) return;
    this.scoreInput = '';
    this.checkoutDarts = '';
  },

  backspace() {
    if (!this.scoringKeypadActive()) return;
    this.scoreInput = String(this.scoreInput || '').slice(0, -1);
    this.checkoutDarts = '';
  },

  disarmUndo() {
    this.undoArmed = false;
    if (this.undoArmTimer) {
      window.clearTimeout(this.undoArmTimer);
      this.undoArmTimer = null;
    }
  },

  armUndo() {
    this.undoArmed = true;
    Alpine.store('toastr').info('Tap Undo again to confirm', 1800);
    if (this.undoArmTimer) window.clearTimeout(this.undoArmTimer);
    this.undoArmTimer = window.setTimeout(() => {
      this.undoArmed = false;
      this.undoArmTimer = null;
    }, 3000);
  },

  async recordThrow(opts = {}) {
    if (this.editingVisitIndex >= 0) return;
    if (!this.canSubmitVisit()) return;
    this.disarmUndo();
    this.saving = true;
    try {
      const checkout = this.scoreOutcome().checkout;
      if (checkout && !opts?.skipCheckoutConfirm) {
        this.saving = false;
        this.openCheckoutConfirmDialog();
        return;
      }
      const body = {
        score: Number(this.scoreInput) || 0,
        checkoutDarts: checkout ? (Number(this.checkoutDarts) || 3) : null,
        checkoutLabel: checkout
          ? (String(this.checkoutLabel || '').trim() || String(Number(this.scoreInput) || 0))
          : undefined,
      };
      const res = await apiFetch(`/challenges/${this.challenge._id}/live/visit`, {
        method: 'POST',
        body: this.isLocalChallenge() ? { ...body, ...this.scoringControlBody() } : body,
      });
      const data = await res.json();
      if (!res.ok || !data?.ok) throw new Error(data?.message || 'Failed to record visit');
      this.challenge = data.challenge || this.challenge;
      this.scoreInput = '';
      this.checkoutDarts = '';
      this.checkoutLabel = '';
      this.ensureChallengeScoringHeartbeatLoop();
    } catch (e) {
      Alpine.store('toastr').error(e.message || 'Unable to record score', 3000);
    } finally {
      this.saving = false;
    }
  },

  async undoThrow() {
    if (!this.canScoreNow() || !this.hasRequiredPlayers()) return;
    if (!this.undoArmed) {
      this.armUndo();
      return;
    }
    this.disarmUndo();
    this.saving = true;
    try {
      const res = await apiFetch(`/challenges/${this.challenge._id}/live/undo`, {
        method: 'POST',
        body: this.isLocalChallenge() ? this.scoringControlBody() : {},
      });
      const data = await res.json();
      if (!res.ok || !data?.ok) throw new Error(data?.message || 'Failed to undo');
      this.challenge = data.challenge || this.challenge;
      this.ensureChallengeScoringHeartbeatLoop();
      this.queueComputerThrowIfNeeded();
      Alpine.store('toastr').success('Last score undone', 1800);
    } catch (e) {
      Alpine.store('toastr').error(e.message || 'Unable to undo', 3000);
    } finally {
      this.saving = false;
    }
  },

  async refreshChallenge() {
    const res = await apiFetch(`/challenges/${this.challengeId}?includeKioskPairing=1`);
    const data = await res.json();
    if (!res.ok || !data?.ok) {
      this.challenge = null;
      throw new Error(data?.message || data?.error || 'Failed to load challenge');
    }
    this.challenge = data.challenge;
    this.challengeKioskPairSecret = String(data?.kioskPairing?.pairSecret || '').trim();
    this.queueComputerThrowIfNeeded();
  },

  async pairChallengeTvFromScorer() {
    this.challengeTvPairMessage = '';
    const code = String(this.challengeTvPairCode || '').trim().toUpperCase();
    if (!code) {
      this.challengeTvPairMessage = 'Enter the pair code from the TV screen.';
      return;
    }
    if (!this.challenge?._id || !this.challengeKioskPairSecret) {
      this.challengeTvPairMessage = 'Pairing is not available. Reload the page and try again.';
      return;
    }
    this.challengeTvPairBusy = true;
    try {
      const res = await apiFetch('/tv-sessions/pair-challenge', {
        method: 'POST',
        body: {
          pairCode: code,
          challengeMatchId: this.challenge._id,
          pairSecret: this.challengeKioskPairSecret,
          displayName: String(this.challengeTvDisplayLabel || '').trim().slice(0, 80),
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || data?.error || 'Pairing failed');
      this.challengeTvPairMessage = 'TV paired. The display should update shortly.';
    } catch (e) {
      this.challengeTvPairMessage = e?.message || 'Pairing failed';
    } finally {
      this.challengeTvPairBusy = false;
    }
  },

  async loadChallengeSession() {
    if (!this.$store.features.enabled('challengeSessions') || !this.challenge?._id) {
      this.session = null;
      return;
    }
    if (this.isLocalChallenge()) {
      this.session = null;
      return;
    }
    try {
      const res = await apiFetch(`/challenges/${this.challenge._id}/session`);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || 'Failed to load session');
      this.session = data.session || null;
    } catch {
      this.session = null;
    }
  },

  challengeEventUrl() {
    const token = encodeURIComponent(getAccessToken() || '');
    const apiBase = String(window.__API_BASE__ || '').replace(/\/+$/, '');
    const path = `/api/challenges/${this.challenge?._id}/events?access_token=${token}`;
    return apiBase ? `${apiBase}${path}` : path;
  },

  ensureChallengeEventStream() {
    if (this.eventSource || !this.challenge?._id || !window.EventSource) return;
    try {
      this.eventSource = new EventSource(this.challengeEventUrl());
      const handler = (event) => {
        const payload = JSON.parse(event.data || '{}');
        this.eventStreamHealthy = true;
        if (payload?.challenge) {
          this.challenge = payload.challenge;
          this.queueComputerThrowIfNeeded();
        }
      };
      for (const evt of ['challenge:update', 'challenge:status', 'challenge:score', 'challenge:live-score', 'challenge:undo', 'challenge:next-leg']) {
        this.eventSource.addEventListener(evt, handler);
      }
      this.eventSource.onopen = () => { this.eventStreamHealthy = true; };
      this.eventSource.onerror = () => { this.eventStreamHealthy = false; };
    } catch {
      this.eventStreamHealthy = false;
    }
  },

  async ensureChallengeScoringClaim(force = false) {
    const admin = this.$store.session?.me?.role === 'admin';
    if (!this.isLocalChallenge() || !this.challenge?._id || (!this.isParticipant() && !admin)) return false;
    const owner = this.currentChallengeScoringControl();
    if (!force && owner && String(owner.deviceId || '') === String(this.scoringDeviceId || '')) return true;
    try {
      const res = await apiFetch(`/challenges/${this.challenge._id}/live/control/claim`, {
        method: 'POST',
        body: this.scoringControlBody({ force }),
      });
      const data = await res.json();
      if (!res.ok || !data?.ok) {
        if (data?.challenge) this.challenge = data.challenge;
        return false;
      }
      this.challenge = data.challenge || this.challenge;
      this.queueComputerThrowIfNeeded();
      return true;
    } catch {
      return false;
    }
  },

  async takeChallengeScoringControl() {
    this.saving = true;
    try {
      const ok = await this.ensureChallengeScoringClaim(true);
      if (!ok) throw new Error('Unable to take scoring control');
      Alpine.store('toastr').success('Scoring control moved to this device', 2500);
      this.ensureChallengeScoringHeartbeatLoop();
    } catch (e) {
      Alpine.store('toastr').error(e.message || 'Unable to take control', 3000);
    } finally {
      this.saving = false;
    }
  },

  async releaseChallengeScoringControl() {
    if (!this.isLocalChallenge() || !this.challenge?._id) return;
    const admin = this.$store.session?.me?.role === 'admin';
    if (!admin && !this.isChallengeScoringControllerOwner()) return;
    this.saving = true;
    try {
      const res = await apiFetch(`/challenges/${this.challenge._id}/live/control/release`, {
        method: 'POST',
        body: this.scoringControlBody(),
      });
      let data = {};
      try {
        data = await res.json();
      } catch { /* ignore */ }
      if (!res.ok) throw new Error(data?.message || 'Unable to release control');
      this.challenge = data.challenge || this.challenge;
      this.stopChallengeScoringHeartbeatLoop();
      Alpine.store('toastr').success('Scoring lock released — another device can take control', 2800);
    } catch (e) {
      Alpine.store('toastr').error(e.message || 'Unable to release control', 3000);
    } finally {
      this.saving = false;
    }
  },

  stopChallengeScoringHeartbeatLoop() {
    if (this.challengeScoringHeartbeatTimer) {
      window.clearInterval(this.challengeScoringHeartbeatTimer);
      this.challengeScoringHeartbeatTimer = null;
    }
  },

  ensureChallengeScoringHeartbeatLoop() {
    this.stopChallengeScoringHeartbeatLoop();
    if (!this.isLocalChallenge() || !this.challenge?._id || !this.isChallengeScoringControllerOwner()) return;
    if (!this.currentChallengeScoringControl()) return;
    this.challengeScoringHeartbeatTimer = window.setInterval(async () => {
      if (!this.isLocalChallenge() || !this.challenge?._id || !this.isChallengeScoringControllerOwner()) {
        this.stopChallengeScoringHeartbeatLoop();
        return;
      }
      try {
        await apiFetch(`/challenges/${this.challenge._id}/live/control/heartbeat`, {
          method: 'POST',
          body: this.scoringControlBody(),
        });
      } catch { /* ignore */ }
    }, 10_000);
  },

  challengeConsoleUrl() {
    return `/admin/challenges/${this.challengeId}/score`;
  },

  challengeBoardUrl() {
    return `/admin/challenges/${this.challengeId}/board`;
  },

  async toggleFullscreen() {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await document.documentElement.requestFullscreen();
    } catch { /* ignore */ }
  },

  async close() {
    this.teardown();
    if (window.opener) {
      window.close();
      return;
    }
    routerNavigate(this.challengeConsoleUrl());
  },

  jumpTargets() {
    return [
      { key: 'scorer', label: 'Scorer', ref: 'scorerPanel' },
      { key: 'history', label: 'Throw History', ref: 'historyPanel' },
      { key: 'control', label: 'Control', ref: 'controlPanel' },
    ];
  },

  jumpTo(refName) {
    this.jumpMenuOpen = false;
    this.quickConfigOpen = false;
    if (refName === 'historyPanel' && typeof this.openScorerMobileHistory === 'function') {
      this.openScorerMobileHistory();
      return;
    }
    const target = this.$refs?.[refName];
    if (!target || typeof target.getBoundingClientRect !== 'function') return;
    const offsetTop = window.scrollY + target.getBoundingClientRect().top - 60;
    window.scrollTo({ top: Math.max(0, offsetTop), behavior: 'smooth' });
  },

  isKeyboardTargetEditable(target) {
    if (!target || !(target instanceof HTMLElement)) return false;
    const tag = String(target.tagName || '').toLowerCase();
    if (target.isContentEditable) return true;
    if ((tag === 'input' || tag === 'textarea') && target.hasAttribute('readonly')) return false;
    return ['input', 'select', 'textarea'].includes(tag);
  },

  async onScoreKeydown(event) {
    if (this.loading || !this.challenge) return;
    if (event.repeat || event.defaultPrevented) return;
    const activeEl = document.activeElement;
    if (activeEl instanceof HTMLElement && this.isKeyboardTargetEditable(activeEl)) return;
    if (this.isKeyboardTargetEditable(event.target)) return;
    if (event.altKey || event.ctrlKey || event.metaKey) return;

    const key = String(event.key || '');
    if (/^[0-9]$/.test(key)) {
      event.preventDefault();
      event.stopPropagation();
      this.appendDigit(key);
      return;
    }
    if (/^Numpad[0-9]$/.test(key)) {
      event.preventDefault();
      event.stopPropagation();
      this.appendDigit(key.replace('Numpad', ''));
      return;
    }
    if (key === 'Backspace') {
      event.preventDefault();
      event.stopPropagation();
      this.backspace();
      return;
    }
    if (key === 'Escape') {
      if (this.playerStatsOpen) {
        event.preventDefault();
        this.closePlayerStatsDialog();
        return;
      }
      if (this.challengeInfoSheetOpen) {
        event.preventDefault();
        this.closeChallengeInfoSheet();
        return;
      }
      if (this.venueEmbedLeaveDialogOpen) {
        event.preventDefault();
        this.closeVenueEmbedLeaveDialog();
        return;
      }
      if (this.editingVisitIndex >= 0) {
        event.preventDefault();
        this.cancelVisitEdit();
        return;
      }
    }
    if (key === 'Enter' || key === 'NumpadEnter') {
      event.preventDefault();
      if (this.editingVisitIndex >= 0) await this.saveVisitEdit();
      else await this.recordThrow();
    }
  },

  statsCarouselRoster() {
    const out = [];
    const homeId = this.refPlayerId(this.challenge?.challengerPlayerId);
    const awayId = this.refPlayerId(this.challenge?.challengedPlayerId);
    if (homeId) {
      out.push({
        id: homeId,
        name: this.sidePlayerLabel('home'),
        side: 'home',
        avatarUrl: this.sidePlayerAvatar('home'),
      });
    }
    if (awayId) {
      out.push({
        id: awayId,
        name: this.sidePlayerLabel('away'),
        side: 'away',
        avatarUrl: this.sidePlayerAvatar('away'),
      });
    }
    return out;
  },

  computePlayerMatchStats(playerRef) {
    const vid = this.refPlayerId(playerRef);
    if (!vid) return null;
    let darts = 0;
    let points = 0;
    let n180 = 0;
    let n140 = 0;
    let n100 = 0;
    let checkouts = 0;
    let legsWon = 0;
    const homeId = this.refPlayerId(this.challenge?.challengerPlayerId);
    for (const leg of this.liveLegs() || []) {
      if (this.refPlayerId(leg?.winnerPlayerId) === vid) legsWon += 1;
      for (const v of leg?.visits || []) {
        if (this.refPlayerId(v?.playerId) !== vid) continue;
        const dv = Math.max(1, Math.min(3, Number(v.darts) || 3));
        darts += dv;
        if (v.bust) continue;
        const sc = Math.min(180, Math.max(0, Number(v.score) || 0));
        points += sc;
        if (v.checkout) checkouts += 1;
        if (sc === 180) n180 += 1;
        else if (sc >= 140) n140 += 1;
        else if (sc >= 100) n100 += 1;
      }
    }
    if (!legsWon) {
      legsWon = vid === homeId ? this.legPointsChallenger() : this.legPointsChallenged();
    }
    const threeDartAvg = darts > 0 ? (points / darts) * 3 : null;
    return { darts, points, threeDartAvg, n180, n140, n100, checkouts, legsWon };
  },

  ...createScorerMatchScoreboardBindings({
    isLive() {
      return !this.challengeFrozenForScoring();
    },
    sideRole(side) {
      return side === 'home' ? 'Challenger' : 'Challenged';
    },
    legsForSide(side) {
      return side === 'home' ? this.legPointsChallenger() : this.legPointsChallenged();
    },
    centerSubline() {
      return this.challengeHeaderRaceLeftText();
    },
    centerDetail() {
      return this.challengeScorerGameBadge();
    },
    showVisitPills() {
      return true;
    },
    visitPillsForSide(side) {
      return this.lastThreeVisitPillsForChallengeSide(side);
    },
    onTapAvatar(side) {
      const p = side === 'home' ? this.challenge?.challengerPlayerId : this.challenge?.challengedPlayerId;
      const playerId = this.refPlayerId(p);
      if (!playerId) return;
      this.openPlayerStatsForPlayer(playerId);
    },
  }),

  ...createScorerPlayerStatsCarouselMethods(),
}));
